//import { captureException } from '@sentry/react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  createSceneApi,
  deleteCameraPresetOptimisticApi,
  deleteConfiguratorApi,
  deleteSceneOptimisticApi,
  duplicateConfiguratorApi,
  duplicateSceneApi,
  // getConfiguratorCameraPresets,
  getSceneLabelsApi,
  updateCameraPresetOptimistic,
  // updateLabelOptimistic,
  updateViewOptimistic,
  saveConfiguratorAsTemplateApi,
  getSceneViewsApi,
  createBackgroundApi,
  uploadAllThumbnailConfigurator,
  uploadHighResImagesToS3,
  getLabelsApi,
  getOptionsApi,
  createTextureLinkingApi,
  updateTextureLinkingApi,
  getTextureLinkingApi,
  deleteTextureLinkingApi,
} from '../../../api/configuratorApi';
import { cancelDraggablePropUI } from '../../../api/useUnityProp';
import { CONFIGURATOR_UNITY_EVENT } from '../../../components/Configurator/unity/unityCallbacks';
import {
  configuratorBridge,
  configuratorModule,
} from '../../../components/Configurator/unity/unityMethods';
import {
  ADD_CONFIGURATOR_CAMERA_PRESETS_API,
  CREATE_CONFIGURATOR_VIEW_API,
  CREATE_CONFIGURATOR_VIEW_API_V2,
} from '../../../config/constant/apiConstant';
import {
  PRODUCT_CONFIGURATOR_DISPLAY_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_LABEL_OPTION_EDIT_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_LABELS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ADD_OPTION_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_PRODUCTS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_PRODUCT_ADD_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_ROUTE,
  PRODUCT_CONFIGURATOR_SCENES_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEWS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEW_DETAILS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEW_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_TAB_ROUTE,
  PRODUCT_CONFIGURATORS_ROUTE_V2,
  PRODUCT_CONFIGURATOR_WATERMARK_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_FONT_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_SORT_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_DOWNLOAD_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_THEME_SETTINGS_ROUTE,
} from '../../../constants/routePathConstants';
import { apiPostV2 } from '../../../helpers/api';
import { updateDbProp } from '../../../helpers/idb';
import { decimals, generateNewName } from '../../../helpers/jsHelper';
import { dispatcher } from '../../../helpers/projectHelper';
import { errorToastV2, successToastV2, successToast } from '../../../helpers/toastHelper';
import { waitForEventOnce } from '../../../helpers/unityPubSub';
import { getConfigruatorPath } from '../../../helpers/urlHelper';
import { history } from '../../../history';
import { openYesNoDialog } from '../../../hooks/useInfoDialogaActions';
import { getAppState } from '../../store';
import { setDraggablePropLoading } from '../admin/curatorLoaderSlicer';
import {
  resetDraggableProduct,
  setDraggableProductImage,
  setUnityDraggableProductData,
} from '../admin/curatorProductsSlicer';
import { curatorActions } from '../admin/curatorSlicer';
import {
  paginatedProductActions,
  paginatedProductSelectors,
} from '../admin/paginatedProductSlicer';
import {
  configuratorActions,
  selectConfiguratorId,
  selectIs360Configurator,
  selectScenes,
  selectSelectedLabelId,
  selectSelectedSceneId,
  selectSelectedViewId,
  selectSidebarOpen,
  selectCurrentScene,
} from './configurator.slicer';
import {
  configuratorCameraActions,
  selectCameraPresets,
  selectSelectedCameraId,
} from './configuratorCamera.slicer';
import { configuratorSceneLabelsActions } from './configuratorSceneLabels.slicer';
import { configuratorSceneProductsActions } from './configuratorSceneProducts.slicer';
import {
  configuratorSceneViewsActions,
  selectFailedToLoadViews,
  selectSceneViews,
} from './configuratorViews.slicer';
import { initializeConfigurator } from '../../../components/Configurator/unity/initUnity';
import { configuratorUnityObjectPropertiesActions } from './configuratorUnityObjectProperties.slicer';
import { configuratorColorDialogActions } from './configuratorColorDialog.slicer';
import { configuratorLabelDetailsActions } from './configuratorLabelDetails.slicer';
import { configuratorUnityObjectActions } from './configuratorUnityObject.slicer';
import {
  getTransformationData,
  getUrlExtension,
  isV2,
} from '../../../components/Configurator/utils';
import { getJson } from '../../../helpers/axiosHelper';
import { configuratorViewBackgroundActions } from './configuratorViewBackground.slicer';
import { SILO_ROOM_TYPE } from '../../../constants/productConfiguratorConstants';

export const toggleSidebarOpen = (open) => {
  const currentOpen = selectSidebarOpen(getAppState());
  const nextOpen = typeof open === 'boolean' ? open : !currentOpen;
  if (!nextOpen) {
    dispatcher(curatorActions.closeFilter());
  }
  dispatcher(configuratorActions.setSidebarOpen(nextOpen));
};

export const setSidebarTab = (tabName) => {
  dispatcher(configuratorActions.setActiveTab(tabName));
};

export const setTheme = (theme) => {
  dispatcher(configuratorActions.setTheme(theme));
};

export const editScene = ({ sceneId }) => {
  toggleSidebarOpen(true);
  openScene(sceneId);
};

export const duplicateConfigurator = async (configuratorId, otherData) => {
  const result = await duplicateConfiguratorApi({
    configuratorId,
    otherData,
    errorMessage: 'Unable to duplicate configurator. Please try again.',
  });
  if (result?.data?.id) {
    const scenes = selectScenes(getAppState());
    for (const scene of scenes) {
      await addScene({
        duplicate: true,
        sceneId: scene.id,
        configuratorDuplicateId: result?.data?.id,
      });
    }
    const actions = [
      configuratorActions,
      configuratorCameraActions,
      configuratorColorDialogActions,
      configuratorLabelDetailsActions,
      configuratorSceneLabelsActions,
      configuratorSceneProductsActions,
      configuratorUnityObjectActions,
      configuratorUnityObjectPropertiesActions,
      configuratorSceneViewsActions,
    ];
    actions.forEach((a) => {
      dispatcher(a.resetState());
    });
    history.replace(getConfigruatorPath(result.data.id));
  }
};

export const deleteConfigurator = async () => {
  // TODO: prevent scene from saving if configurator is deleted
  const success = await dispatcher(deleteConfiguratorApi());
  if (success?.status) {
    successToast('Configurator deleted successfully');
    history.replace(PRODUCT_CONFIGURATORS_ROUTE_V2);
  }
};

export const addScene = async ({
  duplicate = false,
  configuratorDuplicateId,
  v2 = isV2(),
  showMessage = true,
  sceneId,
} = {}) => {
  toggleSidebarOpen(true);
  const configuratorId = selectConfiguratorId(getAppState());
  const DEFAULT_SCENE_NAME = 'New scene';
  const scenes = selectScenes(getAppState());
  // const lastScene = orderBy(scenes, 'seq_no', 'desc')[0];
  // const lastSceneOrder = lastScene?.seq_no || 0;
  // TODO: api call to create a scene instead of wait random
  // await waitRandom();
  let result = null;
  if (duplicate) {
    // TODO: if we  want to duplicate scene -> get current scene -> replace all unique ids -> name scene as "Curren scene name Copy 1"
    result = await duplicateSceneApi({
      sceneId,
      v2,
      configuratorDuplicateId,
      errorMessage: 'Unable to duplicate scene. Please try again.',
    });
  } else {
    const sceneName = generateNewName({ items: scenes, baseName: DEFAULT_SCENE_NAME });
    result = await createSceneApi({
      name: sceneName,
      // seq_no: lastSceneOrder + 1,
      configuratorId,
      errorMessage: 'Unable to create scene. Please try again.',
    });
  }
  const scene = result?.data;
  if (!scene) {
    return;
  } else {
    !configuratorDuplicateId && successToastV2(`${scene?.name || 'Scene'} created successfully`);
  }
  if (!duplicate) {
    dispatcher(
      configuratorSceneProductsActions.fetchSceneProductsSuccess({
        sceneId: scene.id,
        products: [],
      })
    );
    if (!isV2()) {
      dispatcher(
        configuratorSceneLabelsActions.fetchSceneLabelsSuccess({ sceneId: scene.id, labels: [] })
      );
    }
    dispatcher(
      configuratorSceneViewsActions.fetchSceneViewsSuccess({ sceneId: scene.id, views: [] })
    );
  }
  // this should also be called before first view created for 360 configurator as otherwise it will reset views array
  // create default view for 360 configurator
  const is360Configurator = selectIs360Configurator(getAppState());
  if (is360Configurator && !duplicate && !isV2()) {
    await addView({ sceneId: scene.id, showMessage, shouldOpen: false });
  }
  dispatcher(configuratorActions.addScene(scene));
  !configuratorDuplicateId && selectScene(scene.id);
};

export const deleteScene = async (sceneId) => {
  const currentSceneId = selectSelectedSceneId(getAppState());
  if (currentSceneId === sceneId) {
    const scenes = selectScenes(getAppState());
    const nextSceneToOpen = scenes.filter((s) => s.id !== sceneId)[0];
    if (nextSceneToOpen) {
      openScene(nextSceneToOpen.id, { onlyRedux: true });
    } else {
      configuratorModule.ResetConfiguratorReceiver();
      await waitForEventOnce(CONFIGURATOR_UNITY_EVENT.OnConfiguratorSceneLoaded);
      initializeConfigurator();
    }
  }
  await dispatcher(deleteSceneOptimisticApi(sceneId));
};

export const goToScenes = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  history.replace(getConfigruatorPath(configuratorId));
};

export const selectScene = async (
  sceneId,
  { soft = false, onlyRedux = false, getViews = true } = {}
) => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_ROUTE.replace(':configuratorId', configuratorId).replace(
    ':sceneId',
    sceneId
  );
  dispatcher(configuratorActions.setSceneId(sceneId));
  // if (!onlyRedux) {
  //   loadSceneLabelsWithOptions({ sceneId });
  // }
  if (getViews) {
    dispatcher(getSceneViewsApi({ sceneId, onlyOnce: true }));
  }
  if (onlyRedux) {
    const scenesRoute = PRODUCT_CONFIGURATOR_SCENES_ROUTE.replace(
      ':configuratorId',
      configuratorId
    );
    if (window.location.pathname.startsWith(scenesRoute)) {
      if (!window.location.pathname.startsWith(url)) {
        history.replace(url);
      }
    }
    return;
  }
  // below is url update related logic
  if (soft) {
    if (window.location.pathname.startsWith(url)) {
      return;
    }
  }
  // await initSceneLables(sceneId);
  history.replace(url);
};

export const openScene = selectScene;

export const goToCurrentScene = ({ getViews = true }) => {
  const sceneId = selectSelectedSceneId(getAppState());
  selectScene(sceneId, { getViews });
};

export const openSceneProducts = () => {
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_PRODUCTS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  ).replace(':sceneId', sceneId);

  history.replace(url);
};

export const openAddProduct = () => {
  if (!selectSidebarOpen(getAppState())) {
    toggleSidebarOpen(true);
  }
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_PRODUCT_ADD_ROUTE.replace(
    ':configuratorId',
    configuratorId
  ).replace(':sceneId', sceneId);
  history.replace(url);
};

export const openSceneViews = () => {
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_VIEWS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  ).replace(':sceneId', sceneId);
  history.replace(url);
};

export const openViewSettings = () => {
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const views = selectSceneViews(sceneId)(getAppState());
  const failed = selectFailedToLoadViews(sceneId)(getAppState());
  const view = views[0];
  const viewId = !failed && view?.id ? view.id : 'failed';
  let url = PRODUCT_CONFIGURATOR_SCENE_VIEW_SETTINGS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  )
    .replace(':sceneId', sceneId)
    .replace(':viewId', viewId);
  history.replace(url);
};

export const openSceneLabels = () => {
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_LABELS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  ).replace(':sceneId', sceneId);
  history.replace(url);
};

export const openTab = (tab) => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_TAB_ROUTE.replace(':configuratorId', configuratorId).replace(
    ':tabName',
    tab
  );
  setSidebarTab(tab);
  history.replace(url);
};

export const addView = async ({
  sceneId,
  showMessage = true,
  shouldOpen = true,
  viewName,
  otherData = {},
} = {}) => {
  sceneId = sceneId || selectSelectedSceneId(getAppState());
  const views = selectSceneViews(sceneId)(getAppState());
  // const firstView = views?.[0];
  const DEFAULT_NAME = 'View';
  const viewData = {
    scene: sceneId,
    name: generateNewName({ items: views, baseName: DEFAULT_NAME }),
    ...otherData,
    // ...(firstView?.latest_bg && {
    //   latest_bg: firstView.latest_bg,
    // }),
  };
  if (viewName) {
    viewData['name'] = viewName;
  }
  if (isV2()) {
    const transformationData = await getTransformationData();
    viewData.extra_data = JSON.stringify({
      transformChangedObjectData: transformationData,
    });
  }
  const mainUrl = isV2() ? CREATE_CONFIGURATOR_VIEW_API_V2 : CREATE_CONFIGURATOR_VIEW_API;
  const url = mainUrl.replace(':sceneId', sceneId);
  const result = await apiPostV2(url, {
    data: viewData,
    errorMessage: 'An error occured while creating view. Please try again.',
  }).send();
  const view = result?.data;
  if (!view) {
    return;
  }
  dispatcher(configuratorUnityObjectPropertiesActions.setAreCameraPropertiesChanged(false));
  showMessage && successToastV2(`Your view has been added successfully`);
  dispatcher(configuratorSceneViewsActions.addViewsToScene({ sceneId, views: [view] }));
  if (shouldOpen) {
    openView({ viewId: view.id });
  } else {
    return view;
  }
};

export const duplicateView = async ({ sceneId, isCopy = false, isDuplicate, view } = {}) => {
  sceneId = sceneId || selectSelectedSceneId(getAppState());
  const views = selectSceneViews(sceneId)(getAppState());

  // added this logic to use the transform of selected scene default selected view of the scene.
  // so we bypass the position issue
  // https://app.clickup.com/t/3326989/DEV-34991
  const currentSelectedSceneData = selectCurrentScene(getAppState());
  const currentSelectedSceneView = selectSceneViews(currentSelectedSceneData?.id)(getAppState());

  if (!isDuplicate) {
    view = {
      ...view,
      extra_data: JSON.stringify(currentSelectedSceneView?.extra_data),
    };
  }
  const viewData = {
    ...view,
    name: generateNewName({
      items: views,
      baseName: view.name,
      nameKey: 'name',
    }),
    scene: sceneId,
  };
  Object.keys(viewData).map((val) => {
    if (typeof viewData[val] === 'object') {
      viewData[val] = JSON.stringify(viewData[val]);
    }
    if (!viewData[val] || viewData[val] === 'null') {
      delete viewData[val];
    }
  });
  delete viewData?.id;
  delete viewData?.seq_no;
  delete viewData?.is_update_allowed;
  delete viewData?.created_at;
  delete viewData?.updated_at;
  delete viewData?.is_preview;
  delete viewData?.is_render;
  delete viewData?.is_active;
  delete viewData?.latest_bg_detail;
  delete viewData?.original_bg;
  delete viewData?.latest_bg;
  const mainUrl = isV2() ? CREATE_CONFIGURATOR_VIEW_API_V2 : CREATE_CONFIGURATOR_VIEW_API;
  const url = mainUrl.replace(':sceneId', sceneId);
  const result = await apiPostV2(url, {
    data: viewData,
    errorMessage: `An error occured while ${
      isDuplicate && !isCopy ? 'duplicating' : 'copying'
    } view. Please try again.`,
  }).send();
  const data = result?.data;
  if (!data) {
    return;
  }
  dispatcher(configuratorSceneViewsActions.addViewsToScene({ sceneId, views: [data] }));
  if (viewData?.is_bg && (isDuplicate || isCopy)) {
    let bgDetail = view?.latest_bg_detail;
    if (typeof bgDetail === 'string') {
      bgDetail = JSON.parse(bgDetail);
    }
    const bgFile = view?.latest_bg_detail?.bg_file;
    const bg = await dispatcher(
      createBackgroundApi({
        viewId: data?.id,
        sceneId,
        ...bgDetail,
        errorMessage: 'Unable to get background details',
      })
    );
    if (bgFile && bg) {
      const blob = await fetch(bgFile).then((r) => r.blob());
      const extension = getUrlExtension(bgFile);
      const file = new File([blob], `test.${extension}`, { type: blob.type });
      const result = await uploadAllThumbnailConfigurator({
        obj_id: bg.id,
        file: file.name,
        request_type: 'viewbackground',
      });
      if (result) {
        await uploadHighResImagesToS3({ file, presigned_data: result.presigned_data_bg_file });
        dispatcher(
          configuratorViewBackgroundActions.updateBackground({
            ...bg,
            bg_file: result.presigned_url_bg_file,
          })
        );
        dispatcher(
          configuratorSceneViewsActions.updateView({
            sceneId,
            view: {
              id: data?.id,
              original_bg: bg.id,
              latest_bg: bg.id,
              latest_bg_detail: {
                ...bg,
                bg_file: result.presigned_url_bg_file,
              },
            },
          })
        );
      }
    }
  }
  successToastV2(`View ${isDuplicate && !isCopy ? 'duplicated' : 'copied'} successfully`);
};

export const addCamera = async () => {
  const camera = await waitForEventOnce(
    CONFIGURATOR_UNITY_EVENT.RecieveConfiguratorCameraDetails,
    () => {
      configuratorBridge.GetCurrentCameraModel();
    }
  );
  camera.id = uuidv4();
  camera.cameraName = '';
  camera.__IS_NEW_CAMERA__ = true;
  dispatcher(configuratorCameraActions.setEditCameraData(camera));
};

export const closeEditCamera = () => {
  dispatcher(configuratorCameraActions.setEditCameraData(null));
};

const applyCameraDefaultValues = (camera) => {
  const _camera = { ...camera };
  _camera.focalLength = _camera.focalLength || 0;
  _camera.focusXPos = _camera.focusXPos || 0;
  _camera.focusYPos = _camera.focusYPos || 0;
  _camera.focusZPos = _camera.focusZPos || 0;
  _camera.zTransRot = _camera.zTransRot ? decimals(_camera.zTransRot, 4) : 0;
  return _camera;
};

export const confirmAddNewCamera = async ({
  cameraData,
  successMessage = 'Camera preset added',
}) => {
  const configuratorId = selectConfiguratorId(getAppState());
  cameraData = applyCameraDefaultValues(cameraData);
  cameraData.configurator = configuratorId;
  const url = ADD_CONFIGURATOR_CAMERA_PRESETS_API.replace(':configuratorId', configuratorId);
  const result = await apiPostV2(url, {
    data: cameraData,
    successMessage,
  }).send();
  const camera = result?.data;
  if (camera) {
    dispatcher(configuratorCameraActions.addCameraPreset(camera));
    return camera;
  }
  return false;
};

export const updateCamera = async ({ cameraData }) => {
  cameraData = applyCameraDefaultValues(cameraData);
  return await dispatcher(updateCameraPresetOptimistic({ cameraData }));
};

export const selectCamera = ({ camera }) => {
  dispatcher(configuratorCameraActions.setSelectedCameraId(camera.id));
  configuratorBridge.SetupCameraSettings(camera);
};

export const clearCameraSelection = () => {
  const selectedCameraId = selectSelectedCameraId(getAppState());
  if (!selectedCameraId) return;
  dispatcher(configuratorCameraActions.setSelectedCameraId(null));
};

export const editCamera = ({ camera }) => {
  dispatcher(configuratorCameraActions.setEditCameraData(camera));
};

export const deleteCamera = ({ id }) => {
  openYesNoDialog({
    title: 'Are you sure you want to delete this camera?',
    onButtonClick: () => {
      dispatcher(deleteCameraPresetOptimisticApi(id));
    },
  });
};

export const duplicateCamera = async ({ camera }) => {
  const cameras = selectCameraPresets(getAppState());
  const newCamera = {
    ...camera,
    cameraName: generateNewName({
      items: cameras,
      baseName: camera.cameraName,
      nameKey: 'cameraName',
    }),
    is_default: false,
  };
  const success = await confirmAddNewCamera({
    cameraData: newCamera,
    successMessage: 'Camera preset duplicated',
  });
  if (!success) {
    errorToastV2('An error occured while duplicating camera. Please try again');
  }
};

export const openView = ({ viewId, mode }) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  dispatcher(configuratorCameraActions.setSelectedCameraId(null));
  let url = PRODUCT_CONFIGURATOR_SCENE_VIEW_DETAILS_ROUTE.replace(':configuratorId', configuratorId)
    .replace(':sceneId', sceneId)
    .replace(':viewId', viewId);
  history.replace(url);
};

export const prepareUnityPropDataFull = async ({ prop, trackProgress }) => {
  let config;
  if (trackProgress) {
    config = {
      assetFetchFunc: async ({ file, file_window_door, is_windows_and_doors = false }) => {
        const response = await axios({
          method: 'GET',
          url: is_windows_and_doors ? file_window_door : file,
          responseType: 'blob',
          onDownloadProgress: (event) => {
            console.log(event);
            if (event.type === 'progress' && !is_windows_and_doors) {
              dispatcher(
                paginatedProductActions.setProgress({
                  id: prop.id,
                  total: event.total,
                  loaded: event.loaded,
                })
              );
            }
          },
        });

        if (!response?.data) {
          throw new Error('Unable download prop');
        }

        return response?.data;
      },
    };
  }
  const propData = await updateDbProp(prop, config);
  return prepareUnityPropData({ prop: propData });
};

export const prepareUnityPropData = ({ prop }) => {
  console.log(prop, 'check prop data check');
  // accepts prop from index db
  return {
    id: prop?.id,
    propName: prop?.name,
    locationUrl: prop?.is_windows_and_doors
      ? prop?.file_window_door || prop?.product_file_window_door
      : prop?.file || prop?.product_file,
    // locationUrl: prop.product_file,
    roomLightDatas: [], // TODO
    main_asset_name: prop?.main_asset_name,
    isRoofProp: prop?.is_roof_prop,
    blender_id: prop?.blender_id,
    isExclusiveProp: false, // TODO: we need to remove this property as we don't need it in configurator
    ...(prop?.prop_data_user_file ? { modelType: 'UserImportModel' } : {}),
  };
};

export const addProp = async ({ prop, isRetailAutomation = false }) => {
  try {
    dispatcher(setUnityDraggableProductData(null));
    setDraggablePropLoading(true);
    dispatcher(
      setDraggableProductImage({
        id: prop.id,
        image: prop.thumbnail,
      })
    );
    const propData = await prepareUnityPropDataFull({ prop, trackProgress: true });
    if (isRetailAutomation) {
      propData.originalProp = prop;
      propData.configuratorPropMaxCount = prop.configuratorPropMaxCount;
      propData.isRetailAutomationProp = true;
    }
    dispatcher(setUnityDraggableProductData(propData));
  } catch (e) {
    // captureException(e);
    console.error(e);
    errorToastV2('An error occured while loading product. Please try again.');
    cancelDraggablePropUI();
  } finally {
    setDraggablePropLoading(false);
  }
};

export const getProductCountLightroom = async (propData) => {
  let sceneId = selectSelectedSceneId(getAppState());
  const views = selectSceneViews(sceneId)(getAppState());
  let count = 0;
  let executedLoop = 0;
  // Used for loop as map was causing async behaviour and value is returing as 0 before operation
  for (let i = 0; i < views?.length; i++) {
    if (views[i]?.lightroom && views[i]?.lightroom_detail?.json_file) {
      const lightJson = await getJson(views[i]?.lightroom_detail?.json_file);
      const allMatchingProps = lightJson?.blenderAssetBundleDatas?.filter(
        (data) => parseInt(data?.id) === propData?.id
      );
      // count += allMatchingProps?.length;
      allMatchingProps.map((data) => {
        const objId = parseInt(data.objectIndex) || 0;
        if (count < objId) {
          count = objId;
        }
      });
      executedLoop++;
    }
  }
  if (executedLoop === views?.length) {
    return count;
  } else {
    return 0;
  }
};

export const getTotalPropCountFromAllViews = async (propData) => {
  const currentScene = selectCurrentScene(getAppState());
  let totalProductCount = 0;
  if (currentScene?.json_file) {
    const sceneJson = await getJson(currentScene?.json_file);
    if (sceneJson?.blenderAssetBundleDatas?.length) {
      const allMatchingProps = sceneJson?.blenderAssetBundleDatas?.filter(
        (data) => parseInt(data?.id) === propData?.id
      );
      allMatchingProps.map((data) => {
        const objId = parseInt(data.objectIndex) || 0;
        if (totalProductCount < objId) {
          totalProductCount = objId;
        }
      });
      // totalProductCount += allMatchingProps?.length;
    }
  }
  const viewsMaxProductCount = await getProductCountLightroom(propData);
  // totalProductCount += (await getProductCountLightroom(propData));
  return Math.max(totalProductCount, viewsMaxProductCount);
};

export const getLightCountLifestyle = async (currentLight) => {
  let sceneId = selectSelectedSceneId(getAppState());
  const views = selectSceneViews(sceneId)(getAppState());
  let count = 0;
  let executedLoop = 0;
  // Used for loop as map was causing async behaviour and value is returing as 0 before operation
  for (let i = 0; i < views?.length; i++) {
    if (views[i]?.lightroom && views[i]?.lightroom_detail?.json_file) {
      const lightJson = await getJson(views[i]?.lightroom_detail?.json_file);
      const allLightsIds = lightJson?.roomLightData?.map((data) => parseInt(data.id));
      const maxVal = Math.max(...allLightsIds);
      if (maxVal > count) {
        count = maxVal;
      }
      executedLoop++;
    }
  }
  if (executedLoop === views?.length) {
    return count;
  } else {
    return 0;
  }
};

export const getMaxLightroomCount = async (currentLight) => {
  const currentScene = selectCurrentScene(getAppState());
  let totalLightCount = 0;
  if (currentScene?.json_file) {
    const sceneJson = await getJson(currentScene?.json_file);
    if (sceneJson?.roomLightData?.length) {
      const allLightsIds = sceneJson?.roomLightData?.map((data) => parseInt(data.id));
      const maxVal = Math.max(...allLightsIds);
      if (maxVal > totalLightCount) {
        totalLightCount = maxVal;
      }
      // totalProductCount += allMatchingProps?.length;
    }
  }
  const viewsMaxProductCount = await getLightCountLifestyle(currentLight);
  return Math.max(totalLightCount, viewsMaxProductCount);
};

export const finalizeAddProp = async ({ propData, isRetailAutomation }) => {
  const propId = propData.id;
  const sceneId = selectSelectedSceneId(getAppState());
  let prop = paginatedProductSelectors.selectItem(getAppState(), propId);
  if (isRetailAutomation) {
    prop = propData?.originalProp;
  }
  if (!prop) {
    errorToastV2('An error occured while loading product. Please try again.');
    return;
  }
  let lightData;
  if (prop?.is_light_available && prop?.light_data) {
    lightData = await getJson(prop.light_data);
  }
  const propDataToPass = JSON.parse(JSON.stringify(propData));
  delete propDataToPass?.originalProp;
  if (lightData?.lights?.length) {
    propDataToPass['roomLightDatas'] = lightData.lights;
    for (let i = 0; i < propDataToPass['roomLightDatas']?.length; i++) {
      propDataToPass['roomLightDatas'][i]['maxLightCount'] =
        (await getMaxLightroomCount(propDataToPass['roomLightDatas'][i])) + (i + 1);
    }
  }
  if (!isRetailAutomation) {
    const totalCurrentProductCount = await getTotalPropCountFromAllViews(propData);
    if (totalCurrentProductCount || totalCurrentProductCount === 0) {
      propDataToPass['configuratorPropMaxCount'] = totalCurrentProductCount;
    }
  }
  configuratorBridge.LoadProp(propDataToPass);
  dispatcher(resetDraggableProduct());
  if (!isRetailAutomation) {
    // TODO: this should be async action
    dispatcher(configuratorSceneProductsActions.addProductsToScene({ sceneId, products: [prop] }));
  }
};

export const updateView = ({ view, sceneId }) => {
  dispatcher(configuratorActions.updateView({ view, sceneId }));
};

export const updateCurrentView = (updatedProperties) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const viewId = selectSelectedViewId(getAppState());
  const view = {
    id: viewId,
    ...updatedProperties,
  };
  updateView({ view, sceneId });
};

export const openLightingPreview = ({ lightingId }) => {
  dispatcher(configuratorActions.openLightingPreview({ lightingId }));
};

export const closeLightingPreview = () => {
  dispatcher(configuratorActions.closeLightingPreview());
};

export const selectLighting = async ({ lightingId, roomType }) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const viewId = selectSelectedViewId(getAppState());
  let viewObj = { id: viewId, lightroom: lightingId };
  if (lightingId && isV2()) {
    if (roomType !== SILO_ROOM_TYPE) {
      viewObj.is_bg = false;
      viewObj.is_lifestyle = true;
    } else {
      viewObj.is_lifestyle = false;
    }
  }
  await dispatcher(
    updateViewOptimistic({
      sceneId,
      view: viewObj,
    })
  );
  // dispatcher(configuratorSceneViewsActions.selectLighting({ sceneId, viewId, lightingId }));
};

export const setAddLabelActive = (active) => {
  dispatcher(configuratorActions.setAddLabelActive(active));
};

export const openLabelDetails = async ({ labelId }) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  )
    .replace(':sceneId', sceneId)
    .replace(':labelId', labelId);
  history.replace(url);
};

export const openEditOption = ({ optionId, labelId, confirmed = false }) => {
  // confirmed -> prevents dialong which stops user from opening the option
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  labelId = labelId || selectSelectedLabelId(getAppState());
  const url = PRODUCT_CONFIGURATOR_LABEL_OPTION_EDIT_ROUTE.replace(
    ':configuratorId',
    configuratorId
  )
    .replace(':sceneId', sceneId)
    .replace(':labelId', labelId)
    .replace(':optionId', optionId);
  history.replace(url, { REDIRECT_CONFIRMED: confirmed });
};

export const openAddOption = ({ labelId } = {}) => {
  labelId = labelId || selectSelectedLabelId(getAppState());
  const sceneId = selectSelectedSceneId(getAppState());
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ADD_OPTION_ROUTE.replace(
    ':configuratorId',
    configuratorId
  )
    .replace(':sceneId', sceneId)
    .replace(':labelId', labelId);
  history.replace(url);
};

export const updateLabel = ({ label, sceneId }) => {
  dispatcher(configuratorActions.updateLabel({ label, sceneId }));
};

export const updateCurrentLabel = (updatedProperties) => {
  const sceneId = selectSelectedSceneId(getAppState());
  const labelId = selectSelectedLabelId(getAppState());
  const label = {
    id: labelId,
    ...updatedProperties,
  };
  updateLabel({ label, sceneId });
};

// export const updateCurrentLabelOptimistic = async (updatedProperties) => {
//   const sceneId = selectSelectedSceneId(getAppState());
//   const labelId = selectSelectedLabelId(getAppState());
//   const label = {
//     id: labelId,
//     ...updatedProperties,
//   };
//   await dispatcher(updateLabelOptimistic({ sceneId, ...label }));
// };

// export const removeObjectsFromLabel = async ({ sceneId, objectNames, labelId }) => {
//   const sceneLabels = selectSceneLabels(sceneId)(getAppState());
//   const label = sceneLabels.find((l) => l.id === labelId);
//   const linkedObjects = label.data.linkedObjects.filter((o) => !objectNames.includes(o.objectName));
//   await dispatcher(updateLabelOptimistic({ sceneId, id: label.id, linkedObjects }));
// };

export const openSettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SETTINGS_ROUTE.replace(':configuratorId', configuratorId);
  history.replace(url);
};

export const openDisplaySettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_DISPLAY_SETTINGS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  );
  history.replace(url);
};

export const openWatermarkSettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_WATERMARK_SETTINGS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  );
  history.replace(url);
};

export const openFontSettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_FONT_SETTINGS_ROUTE.replace(':configuratorId', configuratorId);
  history.replace(url);
};

export const openThemeSettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_THEME_SETTINGS_ROUTE.replace(':configuratorId', configuratorId);
  history.replace(url);
};

export const openSortSettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_SORT_SETTINGS_ROUTE.replace(':configuratorId', configuratorId);
  history.replace(url);
};

export const openDownloadSettings = () => {
  const configuratorId = selectConfiguratorId(getAppState());
  const url = PRODUCT_CONFIGURATOR_DOWNLOAD_SETTINGS_ROUTE.replace(
    ':configuratorId',
    configuratorId
  );
  history.replace(url);
};

export const loadSceneLabels = async ({ sceneId, onlyOnce = true, onlyRedux = false }) => {
  // get data from api
  const labels = await dispatcher(getSceneLabelsApi({ sceneId, onlyOnce, onlyRedux }));
  return labels;
  // await wait(5000)
  // // add labels to unity
  // labels.forEach(label => {
  //   if (!label.data?.linkedObjects) return;
  //   const objectNames = label.data.linkedObjects.map(o => o.objectName);
  //   LabelsController.AddRenderersToGroup(label.id, objectNames);
  // })
};

export const getOptions = async ({
  labelId,
  sceneId,
  extraParams = {},
  updateNextPage,
  filterEnabled = true,
}) => {
  if (filterEnabled) {
    extraParams.is_enable = true;
  }
  const response = await getOptionsApi({ labelId, sceneId, extraParams });
  let data = response?.results || [];
  // if (filterEnabled) {
  //   data = data?.filter((val) => (isV2() ? val?.data?.is_enable : val?.is_enable));
  // }
  updateNextPage &&
    updateNextPage({
      labelId,
      sceneId,
      page: parseInt(response?.current_page_number),
      nextLink: response?.next_link,
    });
  return data;
};

export const loadSceneLabelsWithOptions = async ({
  sceneId,
  filterEnabled = true,
  filterEmptyLabels = true,
  updateNextPage,
}) => {
  let labels = await getLabelsApi({
    sceneId,
  });
  if (filterEnabled) {
    labels = labels?.filter((val) => (isV2() ? val?.data?.is_enable : val?.is_enable));
  }
  let labelWithOptions = await Promise.all(
    labels.map(async (label) => {
      if (filterEmptyLabels) {
        label.items = !label?.data?.linkedObjects?.length
          ? []
          : await getOptions({ labelId: label.id, sceneId, filterEnabled, updateNextPage });
      } else {
        label.items = await getOptions({
          labelId: label.id,
          sceneId,
          filterEnabled,
          updateNextPage,
        });
      }
      return label;
    })
  );
  if (filterEmptyLabels) {
    labelWithOptions = labelWithOptions?.filter(
      (val) => val?.data?.linkedObjects?.length && val?.items?.length
    );
  }
  return labelWithOptions;
};

export const saveConfAsTemplate = async (name, configuratorId) => {
  const result = await saveConfiguratorAsTemplateApi({
    configuratorId,
    name,
    errorMessage: 'Unable to save as template',
  });
  return result;
};

export const addOrUpdateTextureLinking = async ({ sceneId, jsonContent, id } = {}) => {
  const configuratorId = selectConfiguratorId(getAppState());
  const func = id ? updateTextureLinkingApi : createTextureLinkingApi;
  const obj = {
    sceneId,
    configuratorId,
  };
  if (id) {
    obj.id = id;
  }
  const result = await func(obj);
  const response = result?.data || result; 
  const presignedData = response?.presigned_data;
  if (presignedData) {
    const file = new Blob([JSON.stringify(jsonContent)], {
      type: 'application/json',
    });
    await uploadHighResImagesToS3({ file, presigned_data: presignedData });
    dispatcher(configuratorActions.setTextureLinkingData(jsonContent));
    dispatcher(configuratorActions.setTextureLinkingRawData(response));
  } else {
    errorToastV2('Something went wrong. Please try again.');
  }
};

export const getTextureLinking = async ({ sceneId }) => {
  const configuratorId = selectConfiguratorId(getAppState());
  const results = await getTextureLinkingApi({ configuratorId, sceneId });
  const rawData = results?.[0];
  if (rawData) {
    dispatcher(configuratorActions.setTextureLinkingRawData(rawData));
    const jsonFile = rawData?.scene_texture_linking_json;
    if (jsonFile) {
      try {
        const jsonData = await getJson(jsonFile);
        dispatcher(configuratorActions.setTextureLinkingData(jsonData));
      } catch {
        dispatcher(configuratorActions.setTextureLinkingData([]));
      }
    }
  } else {
    dispatcher(configuratorActions.setTextureLinkingData([]));
    dispatcher(configuratorActions.setTextureLinkingRawData(null));
  }
};

export const getTextureLinkingInPrototype = async ({
  configuratorId,
  sceneId,
  token,
  isPublic,
}) => {
  const results = await getTextureLinkingApi({ configuratorId, sceneId, token, isPublic });
  const jsonFile = results?.[0]?.scene_texture_linking_json;
  if (jsonFile) {
    try {
      const jsonData = await getJson(jsonFile);
      return jsonData;
    } catch {
      return [];
    }
  }
  return [];
};

export const deleteTextureLinking = async ({ id }) => {
  const response = await deleteTextureLinkingApi({ id });
  if (response?.status) {
    dispatcher(configuratorActions.setTextureLinkingData([]));
    dispatcher(configuratorActions.setTextureLinkingRawData(null));
  }
};
